import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { createChildClient } from '../utils/shopifyLinkHandshake';

/**
 * CI-side page for linking a Shopify store to the signed-in Commerce Index account.
 * Opened from the embedded app via window.open. Needs the CI merchant session (Bearer) AND a
 * fresh Shopify session token vended by the opener per request. It never assumes the opener
 * survives sign-in; it never reads tokens from the URL or storage.
 * Plan note: linking records ownership only. It does not choose, change or grant a plan.
 */
const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api/shopify-app/link';
const ciToken = () => (typeof window === 'undefined' ? '' : (window.sessionStorage.getItem('session_token') || window.localStorage.getItem('ci_session_token') || ''));

const MESSAGES = {
  already_linked_other: 'This store is already linked to a different Commerce Index account.',
  consent_required: 'Please confirm the link using the checkbox.',
  preview_stale: 'Your Commerce Index account changed after this preview. Return to Shopify Admin and press Link again.',
  invalid_proof: 'Shopify could not verify this session. Return to Shopify Admin and press Link again.',
  unauthenticated: 'Your Commerce Index sign-in is not valid. Sign in again, then return to Shopify Admin and press Link again.',
};

export default function ShopifyLinkPage({ clientFactory, http = axios }) {
  const [phase, setPhase] = useState('connecting');   // connecting | need_login | no_opener | preview | linking | done | error
  const [info, setInfo] = useState(null);
  const [accepted, setAccepted] = useState(false);
  const [msg, setMsg] = useState('');
  const client = useRef(null);
  const sessionAtPreview = useRef('');
  const hasCi = !!ciToken();

  const call = useCallback(async (path, purpose, body) => {
    const shopifyToken = await client.current.getToken(purpose);   // fresh per request
    return http.post(`${API}/${path}`, body || {}, { headers: { Authorization: `Bearer ${ciToken()}`, 'X-Shopify-Session': shopifyToken } });
  }, [http]);

// Repaired 2026-11-03: the backend raises plain-English detail strings that
// never matched the snake_case MESSAGES keys, so every rejection (including a
// real sign-in failure) collapsed into the generic retry line. Map the exact
// verified backend details to safe, actionable copy.
const DETAIL_MESSAGES = {
  'Not authenticated': 'Your Commerce Index sign-in is not valid. Sign in again, then return to Shopify Admin and press Link again.',
  'Fresh Shopify session required': 'Shopify could not verify this session. Return to Shopify Admin and press Link again.',
  'Valid Shopify session required': 'Shopify could not verify this session. Return to Shopify Admin and press Link again.',
  'Link unavailable': 'The link service is temporarily unavailable. Wait a few minutes, then return to Shopify Admin and press Link again.',
};

  const fail = useCallback((e) => {
    const code = e?.response?.data?.status || e?.response?.data?.detail?.status || e?.response?.data?.detail;
    if (e?.message === 'NO_OPENER' || e?.message === 'HANDSHAKE_TIMEOUT' || e?.message === 'TOKEN_TIMEOUT') { setPhase('no_opener'); return; }
    setMsg(MESSAGES[code] || DETAIL_MESSAGES[code] || 'Linking was not completed. Return to Shopify Admin and press Link again.'); setPhase('error');
  }, []);

  useEffect(() => {
    if (!hasCi) { setPhase('need_login'); return undefined; }
    const c = (clientFactory || (() => createChildClient({ origin: window.location.origin, getOpener: () => window.opener })))();
    client.current = c;
    const onMsg = (ev) => c.handleMessage(ev);
    window.addEventListener('message', onMsg);
    let live = true;
    (async () => {
      try {
        await c.connect();
        const r = await call('preview', 'preview');
        if (!live) return;
        sessionAtPreview.current = ciToken(); setInfo(r.data); setPhase('preview');
      } catch (e) { if (live) fail(e); }
    })();
    return () => { live = false; window.removeEventListener('message', onMsg); };
  }, [hasCi, call, fail, clientFactory]);

  // A stale preview must never be confirmed under another sign-in: drop it if the CI session changes or is cleared.
  useEffect(() => {
    const watch = () => { if (ciToken() !== sessionAtPreview.current) { setInfo(null); setAccepted(false); setMsg('Your Commerce Index session changed. Return to Shopify Admin and press Link again.'); setPhase('error'); } };
    window.addEventListener('storage', watch);
    return () => window.removeEventListener('storage', watch);
  }, []);

  const confirm = async () => {
    if (!accepted || !info) return;
    if (ciToken() !== sessionAtPreview.current) { setInfo(null); setAccepted(false); setMsg('Your Commerce Index session changed. Return to Shopify Admin and press Link again.'); setPhase('error'); return; }   // CI_SESSION_CHANGED
    setPhase('linking');
    try { await call('confirm', 'confirm', { accepted: true, shop_gid: info.shop_gid, merchant_ref: info.merchant_ref }); setPhase('done'); } catch (e) { fail(e); }
  };

  const box = 'max-w-xl mx-auto mt-16 p-6 bg-white border border-slate-200 rounded-lg';
  return (
    <main className={box} data-testid="shopify-link-page">
      <h1 className="text-xl font-semibold mb-3">Link your Shopify store</h1>
      {phase === 'connecting' && <p>Checking your Shopify session...</p>}
      {phase === 'need_login' && (<p>Sign in to Commerce Index first. <a className="underline" href="/login">Sign in</a>. After signing in, return to Shopify Admin and press Link again.</p>)}
      {phase === 'no_opener' && <p>This window lost its connection to Shopify Admin. Return to Shopify Admin and press Link again.</p>}
      {phase === 'preview' && info && (
        <div>
          <p className="mb-2">Store: <strong>{info.shop_domain}</strong></p>
          <p className="mb-2 text-sm text-slate-600">Store ID: {info.shop_gid}</p>
          {info.state === 'linked_to_you' && <p className="mb-2">This store is already linked to your account.</p>}
          {info.state === 'linked_elsewhere' && <p className="mb-2">This store is linked to a different Commerce Index account.</p>}
          {info.state !== 'linked_to_you' && info.state !== 'linked_elsewhere' && (
            <>
              <label className="flex gap-2 items-start mb-3">
                <input type="checkbox" checked={accepted} onChange={(e) => setAccepted(e.target.checked)} />
                <span>I confirm I own this store and want to link it to my signed-in Commerce Index account.</span>
              </label>
              <p className="mb-3 text-sm text-slate-600">Linking records ownership only. It does not change your plan or billing, and any existing Stripe subscription stays as it is.</p>
              <button type="button" disabled={!accepted} onClick={confirm} className="px-4 py-2 bg-slate-900 text-white rounded disabled:opacity-40">Link store</button>
            </>
          )}
        </div>
      )}
      {phase === 'linking' && <p>Linking...</p>}
      {phase === 'done' && <p>Linked. You can close this window and return to Shopify Admin.</p>}
      {phase === 'error' && <p role="alert">{msg}</p>}
    </main>
  );
}
